import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  Icon,
  Message,
  Segment,
  Dimmer,
  Loader,
  Table,
  Divider,
  Label,
  Grid
} from 'semantic-ui-react';
import {
  fetchSecurityDefinition,
  validateSecurityDefinition,
  clearError,
  selectCurrentSecurityDefinition,
  selectIsLoading,
  selectError,
  selectValidationResult,
  selectIsValidating
} from '../../slices/securityRegistrySlice';

import { AuthStdFooter } from "../../utils/auth-std-footer";
/**
 * SecurityDefinitionDetail component for displaying the details of a security definition
 * Mirrors PathDefinitionDetail structure for consistency
 * @returns {JSX.Element} SecurityDefinitionDetail component
 */
const SecurityDefinitionDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { name } = useParams();

  // Get security definition data from Redux
  const currentSecurityDefinition = useSelector(selectCurrentSecurityDefinition);
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectError);
  const validationResult = useSelector(selectValidationResult);
  const isValidating = useSelector(selectIsValidating);

  // Local state
  const [localError, setLocalError] = useState(null);
  const [showValidationDetails, setShowValidationDetails] = useState(false);

  // Fetch security definition data
  useEffect(() => {
    if (name) {
      dispatch(fetchSecurityDefinition(name));
    }
  }, [dispatch, name]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
      setTimeout(() => {
        dispatch(clearError());
        setLocalError(null);
      }, 5000);
    }
  }, [dispatch, error]);

  // Run server validation when security definition loads
  useEffect(() => {
    if (currentSecurityDefinition) {
      console.log('SecurityDefinitionDetail: Running server validation for:', currentSecurityDefinition.name);
      dispatch(validateSecurityDefinition(currentSecurityDefinition));
    }
  }, [dispatch, currentSecurityDefinition]);

  // Handle back button click
  const handleBack = () => {
    navigate('/admin/security-registry');
  };

  // Helper functions
  const getCriticalityColor = (criticality) => {
    switch (criticality) {
      case 'CRITICAL': return 'red';
      case 'HIGH': return 'orange';
      case 'MEDIUM': return 'yellow';
      case 'LOW': return 'green';
      default: return 'grey';
    }
  };

  const getCriticalityIcon = (criticality) => {
    switch (criticality) {
      case 'CRITICAL': return 'exclamation triangle';
      case 'HIGH': return 'warning sign';
      case 'MEDIUM': return 'info circle';
      case 'LOW': return 'check circle';
      default: return 'question circle';
    }
  };

  const getEntityTypeIcon = (entityType) => {
    switch (entityType) {
      case 'Opportunity': return 'dollar sign';
      case 'Application': return 'file alternate';
      case 'Message': return 'comment';
      case 'BookProfile': return 'book';
      default: return 'file';
    }
  };

  // Transform operations object to accessControl array for display
  const getAccessControlRules = (securityDefinition) => {
    if (!securityDefinition) return [];

    // If already in accessControl format, use it
    if (securityDefinition.accessControl && Array.isArray(securityDefinition.accessControl)) {
      return securityDefinition.accessControl;
    }

    // Transform operations object to accessControl array
    if (securityDefinition.operations) {
      return Object.entries(securityDefinition.operations).map(([operation, config]) => ({
        operation: operation,
        requiredRoles: config.requiresRole || config.requiredRoles || [],
        conditions: [], // Operations don't typically have conditions in current format
        description: `${operation} operation rules`,
        requiresEncryption: config.requiresEncryption || false,
        auditLevel: config.auditLevel || 'BASIC',
        requiresApproval: config.requiresApproval || false,
        checksumValidation: config.checksumValidation || false,
        versionControl: config.versionControl || false,
        cascadeValidation: config.cascadeValidation || false,
        dataFiltering: config.dataFiltering || false
      }));
    }

    return [];
  };

  // Transform validations object to validationRules array for display
  const getValidationRules = (securityDefinition) => {
    if (!securityDefinition) return [];

    // If already in validationRules format, use it
    if (securityDefinition.validationRules && Array.isArray(securityDefinition.validationRules)) {
      return securityDefinition.validationRules;
    }

    // Transform validations object to validationRules array
    if (securityDefinition.validations) {
      return Object.entries(securityDefinition.validations).map(([field, config]) => ({
        field: field,
        type: config.type?.toUpperCase() || 'REQUIRED',
        parameters: JSON.stringify({
          required: config.required,
          min: config.min,
          max: config.max,
          maxLength: config.maxLength,
          pattern: config.pattern,
          values: config.values
        }),
        message: config.description || `Validation rule for ${field}`
      }));
    }

    return [];
  };


  return (
    <div className="page-container" style={{ marginBottom: "70px" }}>
      <Container>
        <Segment>
          <Header as="h2">
            <Icon name="shield alternate" />
            <Header.Content>
              Security Definition Details
              <Header.Subheader>Viewing details for security definition: {name}</Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          {/* Navigation buttons */}
          <div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon
              labelPosition="left"
              onClick={handleBack}
            >
              <Icon name="arrow left" />
              Back to Security Registry
            </Button>
            <div>
              <Button
                as={Link}
                to={`/admin/security-registry/${name}/edit`}
                color="yellow"
                icon
                labelPosition="left"
                style={{ marginRight: '0.5em' }}
              >
                <Icon name="edit" />
                Edit
              </Button>
            </div>
          </div>

          {/* Error message */}
          {localError && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{localError}</p>
            </Message>
          )}

          {/* Validation Status Banner - Server Validation */}
          {isValidating && (
            <Message icon style={{ marginBottom: '1.5em' }}>
              <Icon name="spinner" loading />
              <Message.Content>
                <Message.Header>Validating Security Definition</Message.Header>
                <p>Running server-side validation checks...</p>
              </Message.Content>
            </Message>
          )}

          {!isValidating && validationResult && (
            <Message
              color={validationResult.color}
              icon
              style={{ marginBottom: '1.5em' }}
            >
              <Icon name={validationResult.icon} />
              <Message.Content>
                <Message.Header>
                  Current Status: {validationResult.status}
                  <span style={{ float: 'right', fontSize: '0.9em', fontWeight: 'normal' }}>
                    Score: {validationResult.score}/100
                  </span>
                </Message.Header>
                <p>{validationResult.message}</p>

                {(validationResult.errors?.length > 0 || validationResult.warnings?.length > 0) && (
                  <div style={{ marginTop: '0.5em' }}>
                    <Button
                      size="mini"
                      basic
                      onClick={() => setShowValidationDetails(!showValidationDetails)}
                      style={{ marginBottom: '0.5em' }}
                    >
                      <Icon name={showValidationDetails ? 'chevron up' : 'chevron down'} />
                      {showValidationDetails ? 'Hide' : 'Show'} Details
                    </Button>

                    {showValidationDetails && (
                      <div style={{
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        padding: '0.8em',
                        borderRadius: '4px',
                        marginTop: '0.5em'
                      }}>
                        {validationResult.errors?.length > 0 && (
                          <div style={{ marginBottom: '0.5em' }}>
                            <strong style={{ color: '#db2828' }}>
                              <Icon name="times circle" />
                              Critical Issues ({validationResult.errors.length}):
                            </strong>
                            <ul style={{ margin: '0.3em 0 0 1.5em', color: '#db2828' }}>
                              {validationResult.errors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {validationResult.warnings?.length > 0 && (
                          <div>
                            <strong style={{ color: '#f2711c' }}>
                              <Icon name="exclamation triangle" />
                              Warnings ({validationResult.warnings.length}):
                            </strong>
                            <ul style={{ margin: '0.3em 0 0 1.5em', color: '#f2711c' }}>
                              {validationResult.warnings.map((warning, index) => (
                                <li key={index}>{warning}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Message.Content>
            </Message>
          )}

          {/* Security definition details */}
          {isLoading ? (
            <Segment>
              <Dimmer active inverted>
                <Loader inverted>Loading Security Definition</Loader>
              </Dimmer>
              <div style={{ height: '200px' }}></div>
            </Segment>
          ) : !currentSecurityDefinition ? (
            <Message warning>
              <Message.Header>Security Definition Not Found</Message.Header>
              <p>
                The security definition "{name}" could not be found. It may have been deleted or renamed.
              </p>
            </Message>
          ) : (
            <div>
              {/* Basic Information */}
              <Segment>
                <Header as="h3" dividing>Basic Information</Header>
                <Grid columns={2}>
                  <Grid.Row>
                    <Grid.Column>
                      <Table definition>
                        <Table.Body>
                          <Table.Row>
                            <Table.Cell width={4}>Name</Table.Cell>
                            <Table.Cell>{currentSecurityDefinition.name}</Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Description</Table.Cell>
                            <Table.Cell>{currentSecurityDefinition.description || 'No description'}</Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Entity Type</Table.Cell>
                            <Table.Cell>
                              <Label>
                                <Icon name={getEntityTypeIcon(currentSecurityDefinition.entityType)} />
                                {currentSecurityDefinition.entityType}
                              </Label>
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Criticality</Table.Cell>
                            <Table.Cell>
                              <Label color={getCriticalityColor(currentSecurityDefinition.criticality)}>
                                <Icon name={getCriticalityIcon(currentSecurityDefinition.criticality)} />
                                {currentSecurityDefinition.criticality}
                              </Label>
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Status</Table.Cell>
                            <Table.Cell>
                              {currentSecurityDefinition.active ? (
                                <Label color="green">Active</Label>
                              ) : (
                                <Label color="red">Inactive</Label>
                              )}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Version</Table.Cell>
                            <Table.Cell>{currentSecurityDefinition.version || '1.0.0'}</Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Created At</Table.Cell>
                            <Table.Cell>
                              {currentSecurityDefinition.createdAt ?
                                new Date(currentSecurityDefinition.createdAt).toLocaleString() :
                                'N/A'}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Updated At</Table.Cell>
                            <Table.Cell>
                              {currentSecurityDefinition.updatedAt ?
                                new Date(currentSecurityDefinition.updatedAt).toLocaleString() :
                                'N/A'}
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table>
                    </Grid.Column>
                    <Grid.Column>
                      <Message info>
                        <Message.Header>About Security Definitions</Message.Header>
                        <p>
                          Security definitions define access control rules and validation requirements
                          for transactional entities in the system.
                        </p>
                        <p>
                          Each security definition specifies who can access what operations on specific
                          entity types, with different criticality levels determining the strictness of enforcement.
                        </p>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>

              {/* Access Control Rules */}
              <Segment>
                <Header as="h3" dividing>Access Control Rules</Header>
                {(() => {
                  const accessControlRules = getAccessControlRules(currentSecurityDefinition);
                  return accessControlRules.length > 0 ? (
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Operation</Table.HeaderCell>
                          <Table.HeaderCell>Required Roles</Table.HeaderCell>
                          <Table.HeaderCell>Audit Level</Table.HeaderCell>
                          <Table.HeaderCell>Additional Settings</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {accessControlRules.map((rule, index) => (
                          <Table.Row key={index}>
                            <Table.Cell>
                              <Label color="blue">{rule.operation}</Label>
                            </Table.Cell>
                            <Table.Cell>
                              {rule.requiredRoles && rule.requiredRoles.length > 0 ? (
                                <div>
                                  {rule.requiredRoles.map((role, roleIndex) => (
                                    <Label key={roleIndex} style={{ margin: '0.2em' }}>
                                      {role}
                                    </Label>
                                  ))}
                                </div>
                              ) : (
                                <em>No specific roles required</em>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Label color="teal">{rule.auditLevel}</Label>
                            </Table.Cell>
                            <Table.Cell>
                              <div>
                                {rule.requiresEncryption && (
                                  <Label size="mini" color="red" style={{ margin: '0.1em' }}>
                                    <Icon name="lock" />
                                    Encryption
                                  </Label>
                                )}
                                {rule.requiresApproval && (
                                  <Label size="mini" color="orange" style={{ margin: '0.1em' }}>
                                    <Icon name="checkmark" />
                                    Approval
                                  </Label>
                                )}
                                {rule.checksumValidation && (
                                  <Label size="mini" color="blue" style={{ margin: '0.1em' }}>
                                    <Icon name="shield" />
                                    Checksum
                                  </Label>
                                )}
                                {rule.versionControl && (
                                  <Label size="mini" color="green" style={{ margin: '0.1em' }}>
                                    <Icon name="history" />
                                    Versioning
                                  </Label>
                                )}
                                {rule.dataFiltering && (
                                  <Label size="mini" color="purple" style={{ margin: '0.1em' }}>
                                    <Icon name="filter" />
                                    Filtering
                                  </Label>
                                )}
                                {!rule.requiresEncryption && !rule.requiresApproval && !rule.checksumValidation && !rule.versionControl && !rule.dataFiltering && (
                                  <em>None</em>
                                )}
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  ) : (
                    <Message warning>
                      <p>No access control rules defined for this security definition.</p>
                    </Message>
                  );
                })()}
              </Segment>

              {/* Validation Rules */}
              <Segment>
                <Header as="h3" dividing>Validation Rules</Header>
                {(() => {
                  const validationRules = getValidationRules(currentSecurityDefinition);
                  return validationRules.length > 0 ? (
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Field</Table.HeaderCell>
                          <Table.HeaderCell>Rule Type</Table.HeaderCell>
                          <Table.HeaderCell>Parameters</Table.HeaderCell>
                          <Table.HeaderCell>Error Message</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {validationRules.map((rule, index) => (
                          <Table.Row key={index}>
                            <Table.Cell>
                              <strong>{rule.field}</strong>
                            </Table.Cell>
                            <Table.Cell>
                              <Label color="teal">{rule.type}</Label>
                            </Table.Cell>
                            <Table.Cell>
                              {rule.parameters ? (
                                <code style={{
                                  backgroundColor: '#f8f8f8',
                                  padding: '0.2em 0.4em',
                                  borderRadius: '3px',
                                  fontSize: '0.9em'
                                }}>
                                  {typeof rule.parameters === 'string' ? rule.parameters : JSON.stringify(rule.parameters)}
                                </code>
                              ) : (
                                <em>None</em>
                              )}
                            </Table.Cell>
                            <Table.Cell>{rule.message || 'Default validation error'}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  ) : (
                    <Message warning>
                      <p>No validation rules defined for this security definition.</p>
                    </Message>
                  );
                })()}
              </Segment>

              {/* Audit Configuration */}
              <Segment>
                <Header as="h3" dividing>Audit Configuration</Header>
                <Table definition>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell width={4}>Audit Enabled</Table.Cell>
                      <Table.Cell>
                        {currentSecurityDefinition.auditConfig?.enabled ? (
                          <Label color="green">
                            <Icon name="check" />
                            Enabled
                          </Label>
                        ) : (
                          <Label color="red">
                            <Icon name="times" />
                            Disabled
                          </Label>
                        )}
                      </Table.Cell>
                    </Table.Row>
                    {currentSecurityDefinition.auditConfig?.enabled && (
                      <>
                        <Table.Row>
                          <Table.Cell>Audit Level</Table.Cell>
                          <Table.Cell>
                            <Label color="blue">
                              {currentSecurityDefinition.auditConfig.level || 'STANDARD'}
                            </Label>
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Retention Period</Table.Cell>
                          <Table.Cell>
                            {currentSecurityDefinition.auditConfig.retentionDays || 90} days
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Include Sensitive Data</Table.Cell>
                          <Table.Cell>
                            {currentSecurityDefinition.auditConfig.includeSensitiveData ? (
                              <Label color="orange">Yes</Label>
                            ) : (
                              <Label color="green">No</Label>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      </>
                    )}
                  </Table.Body>
                </Table>
              </Segment>

              {/* Metadata */}
              <Segment>
                <Header as="h3" dividing>Metadata</Header>
                <Table definition>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell width={4}>Checksum</Table.Cell>
                      <Table.Cell>
                        <code style={{
                          backgroundColor: '#f8f8f8',
                          padding: '0.2em 0.4em',
                          borderRadius: '3px',
                          fontSize: '0.9em'
                        }}>
                          {currentSecurityDefinition.checksum || 'N/A'}
                        </code>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Tags</Table.Cell>
                      <Table.Cell>
                        {currentSecurityDefinition.tags && currentSecurityDefinition.tags.length > 0 ? (
                          <div>
                            {currentSecurityDefinition.tags.map((tag, index) => (
                              <Label key={index} style={{ margin: '0.2em' }}>
                                {tag}
                              </Label>
                            ))}
                          </div>
                        ) : (
                          <em>No tags</em>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </Segment>
            </div>
          )}
        </Segment>
      </Container>
      {AuthStdFooter()}
    </div>
  );
};

export default SecurityDefinitionDetail;
